import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { fetchJsonCached, peekCachedJson, invalidateCachedJson } from "./apiCache";
import { useTheme } from "./useTheme";


// ─── constants ────────────────────────────────────────────────────────────────

const SP = "cubic-bezier(0.2,0.9,0.2,1)";
const SP_OVER = "cubic-bezier(0.34,1.56,0.64,1)";

const TYPE_META = {
  "лекция":       { label: "Лекции",        bg: "#fffbeb", text: "#b45309", border: "#fde68a" },
  "практическая": { label: "Практики",      bg: "#ecfdf5", text: "#047857", border: "#a7f3d0" },
  "лабораторная": { label: "Лабы",          bg: "#f5f3ff", text: "#6d28d9", border: "#ddd6fe" },
  "урок":         { label: "Уроки",         bg: "#f0f9ff", text: "#0369a1", border: "#bae6fd" },
  "экзамен":      { label: "Экзамены",      bg: "#fff7ed", text: "#c2410c", border: "#fed7aa" },
  "консультация": { label: "Консультации",  bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
};

const PLAN_TYPE_COLOR = {
  "лекция":       "#7E9CC4",
  "практическая": "#6DB89A",
  "лабораторная": "#9E8DC8",
  "урок":         "#5AAAC0",
};

const MONTH_SHORT = {
  "01": "Янв", "02": "Фев", "03": "Мар", "04": "Апр",
  "05": "Май", "06": "Июн", "07": "Июл", "08": "Авг",
  "09": "Сен", "10": "Окт", "11": "Ноя", "12": "Дек",
};

const TYPE_ORDER = ["лекция", "урок", "практическая", "лабораторная", "консультация", "экзамен"];

const ACAD_MONTH_ORDER = ["09","10","11","12","01","02","03","04","05","06","07","08"];


// ─── hooks ────────────────────────────────────────────────────────────────────

function useReducedMotion() {
  const [v, setV] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const h = () => setV(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return v;
}

function useIsMobile() {
  const [v, setV] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const h = () => setV(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return v;
}

// ─── filter sub-components ────────────────────────────────────────────────────


function TypeChips({ types, selected, onToggle, onClear, reduced, isMobile }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", handle);
    return () => document.removeEventListener("pointerdown", handle);
  }, [open]);

  if (!types.length) return null;

  const allOff = selected.size === 0;

  let btnLabel, btnColor, btnBg, btnBorder;
  if (allOff) {
    btnLabel = "Тип занятия";
    btnColor = "#64748b";
    btnBg = "#eef2f7";
    btnBorder = "1.5px solid transparent";
  } else if (selected.size === 1) {
    const t = [...selected][0];
    const meta = TYPE_META[t];
    btnLabel = meta?.label || t;
    btnColor = meta?.text || "#1d4ed8";
    btnBg = meta?.bg || "#eff6ff";
    btnBorder = `1.5px solid ${meta?.border || "#bfdbfe"}`;
  } else {
    btnLabel = `${selected.size} типа`;
    btnColor = "#1d4ed8";
    btnBg = "#eff6ff";
    btnBorder = "1.5px solid #bfdbfe";
  }

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        height: isMobile ? 24 : 28, display: "flex", alignItems: "center", gap: 4,
        padding: isMobile ? "0 8px" : "0 10px", borderRadius: 999, fontSize: isMobile ? 10 : 11,
        fontWeight: 600, letterSpacing: "0.02em", whiteSpace: "nowrap",
        border: btnBorder, background: btnBg, color: btnColor,
        cursor: "pointer", outline: "none",
        transform: (open || !allOff) ? "scale(1.03)" : "scale(1)",
        transition: reduced ? "none" : `all 150ms ${SP}`,
      }}>
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
          <rect x="1" y="1.5" width="4" height="4" rx="1" fill="currentColor" opacity="0.55"/>
          <rect x="7" y="1.5" width="4" height="4" rx="1" fill="currentColor"/>
          <rect x="1" y="6.5" width="4" height="4" rx="1" fill="currentColor"/>
          <rect x="7" y="6.5" width="4" height="4" rx="1" fill="currentColor" opacity="0.55"/>
        </svg>
        {btnLabel}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{
          flexShrink: 0,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: reduced ? "none" : `transform 220ms ${SP_OVER}`,
        }}>
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <div style={{
        position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 30,
        minWidth: 180,
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition: reduced ? "none" : `grid-template-rows 220ms ${SP}`,
        overflow: "hidden",
        borderRadius: 14,
      }}>
        <div style={{
          overflow: "hidden",
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 0 0 1px #e2e8f0, 0 8px 24px -6px rgba(15,23,42,0.14)",
        }}>
          <div style={{ padding: "6px 0" }}>
            <button type="button" onClick={onClear} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", padding: "8px 14px",
              fontSize: 12, fontWeight: allOff ? 700 : 500,
              color: allOff ? "#1d4ed8" : "#64748b",
              background: "none", outline: "none", cursor: "pointer",
              border: "none", borderBottom: "1px solid #f1f5f9",
              textAlign: "left",
            }}>
              <span>Все типы</span>
              {allOff && (
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M2 6.5L5 9.5L11 3.5" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
            {types.map(t => {
              const meta = TYPE_META[t];
              if (!meta) return null;
              const on = selected.has(t);
              return (
                <button key={t} type="button" onClick={() => onToggle(t)} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  width: "100%", padding: "8px 14px",
                  fontSize: 12, fontWeight: on ? 600 : 400,
                  color: on ? meta.text : "#0f172a",
                  background: on ? meta.bg : "none",
                  outline: "none", cursor: "pointer",
                  border: "none",
                  textAlign: "left",
                  transition: reduced ? "none" : `background 120ms ${SP}, color 120ms ${SP}`,
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: on ? meta.text : "#cbd5e1",
                    flexShrink: 0,
                    transition: reduced ? "none" : `background 150ms ${SP}`,
                  }} />
                  <span style={{ flex: 1 }}>{meta.label}</span>
                  {on && (
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
                      <path d="M2 6.5L5 9.5L11 3.5" stroke={meta.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function GroupPicker({ groups, selected, onToggle, onClear, reduced, isMobile }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", handle);
    return () => document.removeEventListener("pointerdown", handle);
  }, [open]);

  if (!groups.length) return null;

  const count = selected.size;
  const label = count === 0 ? "Все группы" : count === 1 ? [...selected][0] : `${count} группы`;
  const active = count > 0;

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        height: isMobile ? 24 : 28, display: "flex", alignItems: "center", gap: 4,
        padding: isMobile ? "0 8px" : "0 10px", borderRadius: 999, fontSize: isMobile ? 10 : 11,
        fontWeight: 600, letterSpacing: "0.02em", whiteSpace: "nowrap",
        border: active ? "1.5px solid #bfdbfe" : "1.5px solid transparent",
        background: active ? "#eff6ff" : "#eef2f7",
        color: active ? "#1d4ed8" : "#64748b",
        cursor: "pointer", outline: "none",
        transform: (open || active) ? "scale(1.03)" : "scale(1)",
        transition: reduced ? "none" : `all 150ms ${SP}`,
      }}>
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
          <path d="M2 3h8M3.5 6h5M5 9h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
        {label}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{
          flexShrink: 0,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: reduced ? "none" : `transform 220ms ${SP_OVER}`,
        }}>
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <div style={{
        position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20,
        minWidth: 160,
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition: reduced ? "none" : `grid-template-rows 220ms ${SP}`,
        overflow: "hidden",
        borderRadius: 14,
      }}>
        <div style={{
          overflow: "hidden",
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 0 0 1px #e2e8f0, 0 8px 24px -6px rgba(15,23,42,0.14)",
        }}>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {active && (
              <button type="button" onClick={onClear} style={{
                display: "flex", alignItems: "center", gap: 6,
                width: "100%", padding: "8px 14px",
                fontSize: 12, color: "#94a3b8", fontWeight: 500,
                background: "none", border: "none", borderBottom: "1px solid #f1f5f9",
                cursor: "pointer", outline: "none", textAlign: "left",
              }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                Сбросить
              </button>
            )}
            {groups.map((g, i) => {
              const checked = selected.has(g);
              return (
                <button key={g} type="button" onClick={() => onToggle(g)} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "9px 14px",
                  fontSize: 13,
                  color: checked ? "#2563eb" : "#0f172a",
                  fontWeight: checked ? 600 : 400,
                  background: checked ? "#f0f7ff" : "none",
                  outline: "none", cursor: "pointer",
                  borderBottom: i < groups.length - 1 ? "1px solid #f8fafc" : "none",
                  border: "none", textAlign: "left",
                }}>
                  {g}
                  {checked && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                      <path d="M2.5 7L5.5 10L11.5 4" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── donut chart ─────────────────────────────────────────────────────────────

function _polar(cx, cy, r, deg) {
  const rad = (deg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function _arcPath(cx, cy, ro, ri, a0, a1) {
  const delta = Math.min(a1 - a0, 359.99);
  if (delta <= 0.01) return "";
  const end = a0 + delta;
  const large = delta > 180 ? 1 : 0;
  const o1 = _polar(cx, cy, ro, a0), o2 = _polar(cx, cy, ro, end);
  const i2 = _polar(cx, cy, ri, end), i1 = _polar(cx, cy, ri, a0);
  return `M${o1.x} ${o1.y} A${ro} ${ro} 0 ${large} 1 ${o2.x} ${o2.y} L${i2.x} ${i2.y} A${ri} ${ri} 0 ${large} 0 ${i1.x} ${i1.y}Z`;
}

// 0% → near-black, 100% → green, >100% → red
function _pctColor(pct) {
  if (pct === null || pct === undefined) return "#64748b";
  if (pct > 100) return "#EF5350";
  const t = Math.max(0, Math.min(pct / 100, 1));
  const r = Math.round(15 + (34 - 15) * t);
  const g = Math.round(23 + (197 - 23) * t);
  const b = Math.round(42 + (94 - 42) * t);
  return `rgb(${r},${g},${b})`;
}

function ArchiveDonut({ items, pairCount, reduced, onSelect, displaySize, selTypes, isMobile, interactive, onHovChange }) {
  const [hov, setHov] = useState(null);
  const SIZE = 150, RO = 63, RI = 41, CX = 75, CY = 75;

  useEffect(() => { onHovChange?.(!!hov); }, [hov]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!interactive) setHov(null); }, [interactive]);

  useEffect(() => {
    if (!hov) return;
    const dismiss = () => setHov(null);
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [hov]);
  const ds = displaySize ?? SIZE;
  const visibleItems = selTypes && selTypes.size > 0
    ? items.filter(x => selTypes.has(x.lesson_type))
    : items;
  const total = visibleItems.reduce((s, x) => s + x.count, 0) || 1;

  let cum = 0;
  const segs = visibleItems.map(item => {
    const span = (item.count / total) * 360;
    const s = {
      lesson_type: item.lesson_type,
      a0: cum, a1: cum + span,
      color: PLAN_TYPE_COLOR[item.lesson_type] || "#94a3b8",
      meta: TYPE_META[item.lesson_type],
      count: item.count,
    };
    cum += span; return s;
  });

  const hovSeg = hov ? segs.find(s => s.lesson_type === hov) : null;

  const onEnter = (type) => (e) => { if (!interactive) return; if (e.pointerType !== "touch") setHov(type); };
  const onLeave = (e) => { if (e.pointerType !== "touch") setHov(null); };
  const onTap = (type) => (e) => {
    if (!interactive) return;
    if (e.pointerType === "touch") { e.stopPropagation(); setHov(p => p === type ? null : type); }
  };

  const tooltipStyle = isMobile
    ? { position: "absolute", top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", zIndex: 1000, background: "#fff", borderRadius: 10, padding: "5px 9px", boxShadow: "0 8px 28px -6px rgba(15,23,42,0.2), 0 0 0 1px #e8edf3", pointerEvents: "none", minWidth: 90, whiteSpace: "nowrap" }
    : { position: "absolute", top: "10%", left: "calc(100% + 10px)", zIndex: 1000, background: "#fff", borderRadius: 10, padding: "9px 14px", boxShadow: "0 8px 28px -6px rgba(15,23,42,0.2), 0 0 0 1px #e8edf3", pointerEvents: "none", minWidth: 110 };

  return (
    <div
      onClick={onSelect}
      onPointerDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      style={{ position: "relative", width: ds, height: ds, flexShrink: 0, cursor: onSelect ? "pointer" : "default", WebkitTapHighlightColor: "transparent" }}
    >
      <svg width={ds} height={ds} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ display: "block", userSelect: "none", WebkitUserSelect: "none" }}>
        <circle cx={CX} cy={CY} r={(RO + RI) / 2} fill="none" stroke="#eaeef3" strokeWidth={RO - RI} />
        {segs.map(seg => {
          const d = _arcPath(CX, CY, RO, RI, seg.a0, seg.a1);
          if (!d) return null;
          return (
            <path key={seg.lesson_type} d={d} fill={seg.color}
              opacity={hov && hov !== seg.lesson_type ? 0.25 : 1}
              onPointerEnter={onEnter(seg.lesson_type)}
              onPointerLeave={onLeave}
              onPointerDown={onTap(seg.lesson_type)}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "default", transition: reduced ? "none" : `opacity 180ms ${SP}` }}
            />
          );
        })}
        {interactive && <circle cx={CX} cy={CY} r={RI} fill="transparent" onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }} />}
        {!hov && (
          <text x={CX} y={CY} textAnchor="middle" dominantBaseline="middle"
            style={{ fontSize: 13, fontWeight: 700, fill: "#64748b", fontFamily: "-apple-system,sans-serif", pointerEvents: "none" }}>
            {pairCount} п.
          </text>
        )}
      </svg>
      {hovSeg && (
        <div style={tooltipStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: hovSeg.color, flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 600, color: hovSeg.meta?.text || hovSeg.color, whiteSpace: "nowrap" }}>
              {hovSeg.meta?.label || hovSeg.lesson_type}
            </span>
          </div>
          <div style={{ fontSize: isMobile ? 17 : 22, fontWeight: 700, color: "#0f172a", lineHeight: 1, letterSpacing: "-0.5px" }}>
            {hovSeg.count}
          </div>
          <div style={{ fontSize: isMobile ? 9 : 10, color: "#94a3b8", marginTop: 4 }}>пар проведено</div>
        </div>
      )}
    </div>
  );
}

function DisciplineDonut({ items, selTypes, reduced, onSelect, displaySize, isMobile, interactive, onHovChange }) {
  const [hov, setHov] = useState(null);
  const [hovOver, setHovOver] = useState(false);
  const [animPct, setAnimPct] = useState(1);

  useEffect(() => { onHovChange?.(!!(hov || hovOver)); }, [hov, hovOver]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!interactive) { setHov(null); setHovOver(false); } }, [interactive]);

  useEffect(() => {
    if (!hov && !hovOver) return;
    const dismiss = () => { setHov(null); setHovOver(false); };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [hov, hovOver]);
  const [pieHovFill, setPieHovFill] = useState(0);
  const [pieTransAnim, setPieTransAnim] = useState(1);
  const [displayPct, setDisplayPct] = useState(null);
  const rafRef = useRef(null);
  const pieHovRef = useRef(null);
  const pieTransRef = useRef(null);
  const countRef = useRef(null);
  const displayPctRef = useRef(null);

  const SIZE = 150, RO = 63, RI = 41, CX = 75, CY = 75;
  const activeItems = items.filter(x => x.archive_hours > 0);
  const selectedItems = selTypes.size === 0
    ? activeItems
    : activeItems.filter(x => selTypes.has(x.lesson_type));
  // PIE mode when: no filter, all types selected, or 2+ types selected
  const allOff = selTypes.size === 0
    || activeItems.every(x => selTypes.has(x.lesson_type))
    || selectedItems.length > 1;
  const filteredItems = selectedItems;
  const filtPlan    = filteredItems.reduce((s, x) => s + x.plan_hours, 0);
  const filtArchive = filteredItems.reduce((s, x) => s + x.archive_hours, 0);
  const overallPct  = filtPlan > 0 ? Math.round(filtArchive / filtPlan * 100) : null;
  const isMulti = !allOff && filteredItems.length > 1;

  // RAF animation — ease-out quint, 700ms
  useEffect(() => {
    if (allOff || reduced) { setAnimPct(1); return; }
    setAnimPct(0);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const duration = 1200;
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min((now - t0) / duration, 1);
      setAnimPct(1 - Math.pow(1 - t, 5));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [filtArchive, filtPlan, allOff, reduced]);

  // PIE segment transition on filter change
  const filteredTypesKey = filteredItems.map(x => x.lesson_type).sort().join(",");
  useEffect(() => {
    if (reduced) { setPieTransAnim(1); return; }
    if (pieTransRef.current) cancelAnimationFrame(pieTransRef.current);
    setPieTransAnim(0);
    const dur = 750, t0 = performance.now();
    const tick = (now) => {
      const t = Math.min((now - t0) / dur, 1);
      setPieTransAnim(1 - Math.pow(1 - t, 3));
      if (t < 1) pieTransRef.current = requestAnimationFrame(tick);
    };
    pieTransRef.current = requestAnimationFrame(tick);
    return () => { if (pieTransRef.current) cancelAnimationFrame(pieTransRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTypesKey, reduced]);

  // Center % counter animation
  useEffect(() => {
    if (countRef.current) cancelAnimationFrame(countRef.current);
    if (overallPct === null) { setDisplayPct(null); displayPctRef.current = null; return; }
    const from = displayPctRef.current ?? 0;
    const to = overallPct;
    if (from === to) { setDisplayPct(to); return; }
    const dur = Math.min(Math.abs(to - from) * 22, 1400);
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min((now - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      const cur = Math.round(from + (to - from) * eased);
      setDisplayPct(cur);
      displayPctRef.current = cur;
      if (t < 1) countRef.current = requestAnimationFrame(tick);
      else displayPctRef.current = to;
    };
    countRef.current = requestAnimationFrame(tick);
    return () => { if (countRef.current) cancelAnimationFrame(countRef.current); };
  }, [overallPct]);

  // PIE hover fill animation
  useEffect(() => {
    if (pieHovRef.current) cancelAnimationFrame(pieHovRef.current);
    if (!allOff || !hov) { setPieHovFill(0); return; }
    setPieHovFill(0);
    const dur = 700, t0 = performance.now();
    const tick = (now) => {
      const t = Math.min((now - t0) / dur, 1);
      setPieHovFill(1 - Math.pow(1 - t, 4));
      if (t < 1) pieHovRef.current = requestAnimationFrame(tick);
    };
    pieHovRef.current = requestAnimationFrame(tick);
    return () => { if (pieHovRef.current) cancelAnimationFrame(pieHovRef.current); };
  }, [allOff, hov]);

  // ── PIE mode ──────────────────────────────────────────────────────
  let pieSegs = [];
  if (allOff) {
    let cum = 0;
    pieSegs = filteredItems.map(item => {
      const span = filtArchive > 0 ? item.archive_hours / filtArchive * 360 : 0;
      const rawPct = item.plan_hours > 0 ? Math.round(item.archive_hours / item.plan_hours * 100) : 0;
      const over = rawPct > 100;
      const pctColor = over ? "#EF5350" : rawPct >= 100 ? "#34C759" : rawPct >= 70 ? "#FF9500" : "#94a3b8";
      const s = { lesson_type: item.lesson_type, a0: cum, a1: cum + span, span,
        color: PLAN_TYPE_COLOR[item.lesson_type] || "#94a3b8", meta: TYPE_META[item.lesson_type],
        rawPct, over, pctColor, archive_hours: item.archive_hours, plan_hours: item.plan_hours };
      cum += span; return s;
    });
  }

  // ── SINGLE-TYPE progress ring ─────────────────────────────────────
  let singleSeg = null;
  if (!allOff && !isMulti && filteredItems.length === 1) {
    const item = filteredItems[0];
    const mainT = Math.min(animPct / 0.7, 1);
    const fillAngle = (filtPlan > 0 ? Math.min(filtArchive / filtPlan, 1) : 0) * 360 * mainT;
    const rawPct = item.plan_hours > 0 ? Math.round(item.archive_hours / item.plan_hours * 100) : 0;
    const over = rawPct > 100;
    const pctColor = over ? "#EF5350" : rawPct >= 100 ? "#34C759" : rawPct >= 70 ? "#FF9500" : "#94a3b8";
    singleSeg = { lesson_type: item.lesson_type, a0: 0, a1: fillAngle, span: fillAngle,
      color: PLAN_TYPE_COLOR[item.lesson_type] || "#94a3b8", meta: TYPE_META[item.lesson_type],
      rawPct, over, pctColor, archive_hours: item.archive_hours, plan_hours: item.plan_hours };
  }

  // ── MULTI-TYPE per-lane ───────────────────────────────────────────
  // Ring divided by plan_hours; each lane fills to own %; gray reveals on hover
  const LG = 0;
  let lanes = [], multiSegs = [];
  if (isMulti && filtPlan > 0) {
    let cum = 0;
    lanes = filteredItems.map(item => {
      const allocAngle = (item.plan_hours / filtPlan) * 360;
      const l = { lesson_type: item.lesson_type, a0: cum, allocAngle,
        color: PLAN_TYPE_COLOR[item.lesson_type] || "#94a3b8", meta: TYPE_META[item.lesson_type],
        archive_hours: item.archive_hours, plan_hours: item.plan_hours };
      cum += allocAngle; return l;
    });
    multiSegs = lanes.map(lane => {
      const usable = Math.max(lane.allocAngle - LG, 0);
      const fillFrac = lane.plan_hours > 0 ? Math.min(lane.archive_hours / lane.plan_hours, 1) : 0;
      const fillAngle = usable * fillFrac * animPct;
      const rawPct = lane.plan_hours > 0 ? Math.round(lane.archive_hours / lane.plan_hours * 100) : 0;
      const over = rawPct > 100;
      const pctColor = over ? "#EF5350" : rawPct >= 100 ? "#34C759" : rawPct >= 70 ? "#FF9500" : "#94a3b8";
      return { lesson_type: lane.lesson_type, a0: lane.a0 + LG / 2, a1: lane.a0 + LG / 2 + fillAngle,
        bgEnd: lane.a0 + LG / 2 + usable, span: fillAngle,
        color: lane.color, meta: lane.meta, rawPct, over, pctColor,
        archive_hours: lane.archive_hours, plan_hours: lane.plan_hours };
    });
  }

  // Overplan red arc — single + multi, sequenced after main fill
  const isOver = !allOff && overallPct !== null && overallPct > 100;
  const overT = isOver ? Math.max(0, (animPct - 0.65) / 0.35) : 0;
  const overAngle = isOver ? Math.min((overallPct - 100) / 100, 1) * 360 * overT : 0;
  const overPath = overAngle > 0.3 ? _arcPath(CX, CY, RO, RI, 0, overAngle) : "";

  const hovSeg = hov ? (isMulti ? multiSegs : allOff ? pieSegs : (singleSeg ? [singleSeg] : []))
    .find(s => s.lesson_type === hov) : null;

  // Pre-compute single-type preview arcs for each multi lane (used on hover)
  const hoverArcs = isMulti ? multiSegs.map(seg => {
    const filledAngle = seg.plan_hours > 0 ? Math.min(seg.archive_hours / seg.plan_hours, 1) * 360 : 0;
    return { ...seg, previewD: filledAngle > 0.3 ? _arcPath(CX, CY, RO, RI, 0, filledAngle) : null };
  }) : [];

  const ds = displaySize ?? SIZE;

  const onEnter = (type) => (e) => { if (!interactive) return; if (e.pointerType !== "touch") setHov(type); };
  const onLeave = (e) => { if (e.pointerType !== "touch") setHov(null); };
  const onTap = (type) => (e) => {
    if (!interactive) return;
    if (e.pointerType === "touch") { e.stopPropagation(); setHov(p => p === type ? null : type); }
  };
  const onEnterOver = (e) => { if (!interactive) return; if (!isMulti && e.pointerType !== "touch") { setHovOver(true); setHov(null); } };
  const onTapOver = (e) => {
    if (!interactive) return;
    if (e.pointerType === "touch" && !isMulti) { e.stopPropagation(); setHovOver(p => !p); setHov(null); }
  };

  const ttBase = { position: "absolute", zIndex: 1000, background: "#fff", borderRadius: 10, padding: isMobile ? "5px 9px" : "9px 14px", boxShadow: "0 8px 28px -6px rgba(15,23,42,0.2), 0 0 0 1px #e8edf3", pointerEvents: "none" };
  const ttStyle = isMobile
    ? { ...ttBase, top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", minWidth: 100, whiteSpace: "nowrap" }
    : { ...ttBase, top: "10%", left: "calc(100% + 10px)", minWidth: 120 };

  return (
    <div
      onClick={onSelect}
      onPointerDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: "relative", width: ds, height: ds, flexShrink: 0,
        cursor: onSelect ? "pointer" : "default",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <svg width={ds} height={ds} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ display: "block", userSelect: "none", WebkitUserSelect: "none" }}>

        {/* Gray background circle — always (for pie/single; fades in for multi on hover) */}
        <circle cx={CX} cy={CY} r={(RO + RI) / 2} fill="none" stroke="#eaeef3" strokeWidth={RO - RI}
          opacity={!isMulti || hov ? 1 : 0}
          style={isMulti ? { transition: reduced ? "none" : `opacity 200ms ${SP}` } : undefined}
        />

        {/* MULTI: per-lane faint allocation backgrounds — always visible, fade on hover */}
        {isMulti && multiSegs.map(seg => {
          const d = _arcPath(CX, CY, RO, RI, seg.a0, seg.bgEnd);
          if (!d) return null;
          return (
            <path key={`bg-${seg.lesson_type}`} d={d} fill={seg.color}
              opacity={hov ? 0 : 0.15}
              onPointerEnter={onEnter(seg.lesson_type)}
              onPointerLeave={onLeave}
              onPointerDown={onTap(seg.lesson_type)}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "default", transition: reduced ? "none" : `opacity 180ms ${SP}` }}
            />
          );
        })}

        {/* MULTI: colored filled portions (fade out on hover for single-type preview) */}
        {isMulti && multiSegs.map(seg => {
          const d = _arcPath(CX, CY, RO, RI, seg.a0, seg.a1);
          if (!d) return null;
          return (
            <path key={`lane-${seg.lesson_type}`} d={d} fill={seg.color}
              opacity={hov ? 0 : 1}
              onPointerEnter={onEnter(seg.lesson_type)}
              onPointerLeave={onLeave}
              onPointerDown={onTap(seg.lesson_type)}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "default", transition: reduced ? "none" : `opacity 180ms ${SP}` }}
            />
          );
        })}

        {/* MULTI hover preview: single-type style for each lane (fade in when that lane hovered) */}
        {isMulti && hoverArcs.map(arc => arc.previewD ? (
          <path key={`prev-${arc.lesson_type}`} d={arc.previewD} fill={arc.color}
            opacity={hov === arc.lesson_type ? 1 : 0}
            onPointerEnter={onEnter(arc.lesson_type)}
            onPointerLeave={onLeave}
            onPointerDown={onTap(arc.lesson_type)}
            onClick={(e) => e.stopPropagation()}
            style={{ cursor: "default", transition: reduced ? "none" : `opacity 200ms ${SP}`, pointerEvents: hov === arc.lesson_type ? "auto" : "none" }}
          />
        ) : null)}

        {/* PIE / SINGLE colored fills */}
        {!isMulti && (allOff ? pieSegs : (singleSeg ? [singleSeg] : [])).map(seg => {
          const g = 0;
          let d;
          if (allOff) {
            const animEnd = seg.a0 + seg.span * pieTransAnim;
            d = seg.span * pieTransAnim > 0.3 ? _arcPath(CX, CY, RO, RI, seg.a0, animEnd) : null;
          } else {
            d = _arcPath(CX, CY, RO, RI, seg.a0 + g, seg.a1 - g);
          }
          if (!d) return null;
          const dimmed = hov !== null && (allOff ? true : hov !== seg.lesson_type);
          return (
            <path key={seg.lesson_type} d={d} fill={seg.color}
              opacity={dimmed ? 0.18 : 1}
              onPointerEnter={onEnter(seg.lesson_type)}
              onPointerLeave={onLeave}
              onPointerDown={onTap(seg.lesson_type)}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "default", transition: reduced ? "none" : `opacity 200ms ${SP}` }}
            />
          );
        })}

        {/* PIE hover: progress fill + overplan red */}
        {allOff && hov && (() => {
          const seg = pieSegs.find(s => s.lesson_type === hov);
          if (!seg) return null;
          const rawFrac = seg.plan_hours > 0 ? seg.archive_hours / seg.plan_hours : 0;
          const fillAngle = Math.min(rawFrac, 1) * 360 * pieHovFill;
          const overAngle = Math.max(rawFrac - 1, 0) > 0 ? Math.min(rawFrac - 1, 1) * 360 * pieHovFill : 0;
          const d = fillAngle > 0.3 ? _arcPath(CX, CY, RO, RI, seg.a0, seg.a0 + fillAngle) : null;
          const overD = overAngle > 0.3 ? _arcPath(CX, CY, RO, RI, seg.a0, seg.a0 + overAngle) : null;
          return <>
            {d && <path key="pie-hov-fill" d={d} fill={seg.color} opacity={1} style={{ pointerEvents: "none" }} />}
            {overD && <path key="pie-hov-over" d={overD} fill="#EF5350" opacity={1} style={{ pointerEvents: "none" }} />}
          </>;
        })()}

        {/* Overplan red */}
        {overPath && <path d={overPath} fill="#EF5350"
          onPointerEnter={onEnterOver}
          onPointerLeave={() => setHovOver(false)}
          onPointerDown={onTapOver}
          onClick={(e) => e.stopPropagation()}
          style={{ cursor: !isMulti ? "default" : "none", pointerEvents: isMulti ? "none" : "auto" }}
        />}

        {interactive && <circle cx={CX} cy={CY} r={RI} fill="transparent" onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }} />}
        {/* Center % */}
        {(() => {
          const hovSeg = allOff && hov ? pieSegs.find(s => s.lesson_type === hov) : null;
          const displayVal = hovSeg ? hovSeg.rawPct : (!hov ? displayPct : null);
          const colorPct = hovSeg ? hovSeg.rawPct : overallPct;
          if (displayVal === null) return null;
          const color = _pctColor(colorPct);
          return (
            <text x={CX} y={CY} textAnchor="middle" dominantBaseline="middle"
              style={{ fontSize: 15, fontWeight: 700, fill: color, fontFamily: "-apple-system,sans-serif", pointerEvents: "none" }}>
              {displayVal}%
            </text>
          );
        })()}
      </svg>

      {hovSeg && (
        <div style={ttStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: hovSeg.color, flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 600, color: hovSeg.meta?.text || hovSeg.color, whiteSpace: "nowrap" }}>
              {hovSeg.meta?.label || hovSeg.lesson_type}
            </span>
          </div>
          {hovSeg.over ? (
            <div style={{ lineHeight: 1, letterSpacing: "-0.5px" }}>
              <span style={{ fontSize: isMobile ? 20 : 26, fontWeight: 700, color: "#EF5350" }}>{hovSeg.rawPct}%</span>
            </div>
          ) : (
            <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 700, color: hovSeg.pctColor, lineHeight: 1, letterSpacing: "-0.5px" }}>
              {hovSeg.rawPct}%
            </div>
          )}
          <div style={{ fontSize: isMobile ? 11 : 13, color: "#94a3b8", marginTop: 4, fontWeight: 500, letterSpacing: "-0.3px" }}>
            {hovSeg.archive_hours}<span style={{ color: "#cbd5e1" }}>/</span>{hovSeg.plan_hours}<span style={{ fontSize: isMobile ? 9 : 11 }}> ч</span>
          </div>
        </div>
      )}

      {hovOver && isOver && !isMulti && (
        <div style={ttStyle}>
          <div style={{ fontSize: 10, fontWeight: 600, color: "#EF5350", marginBottom: 5, whiteSpace: "nowrap" }}>
            Перевыполнение
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 2, lineHeight: 1, letterSpacing: "-0.5px" }}>
            <span style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: "#EF5350" }}>+{overallPct - 100}%</span>
          </div>
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>сверх плана</div>
        </div>
      )}
    </div>
  );
}

// ─── control form tooltip ─────────────────────────────────────────────────────

const CONTROL_FORM_STYLE = {
  "экзамен":   { color: "#c2410c", bg: "#fff7ed", label: "Экзамен" },
  "зачет":     { color: "#15803d", bg: "#f0fdf4", label: "Зачёт" },
  "диф.зачет": { color: "#1d4ed8", bg: "#eff6ff", label: "Диф. зачёт" },
};

function ControlFormTooltip({ form }) {
  const s = CONTROL_FORM_STYLE[form];
  if (!s) return null;
  return (
    <div style={{
      position: "absolute",
      bottom: "calc(100% + 6px)",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 400,
      background: "#fff",
      borderRadius: 10,
      padding: "6px 12px",
      boxShadow: "0 4px 20px -4px rgba(15,23,42,0.18), 0 0 0 1px #e2e8f0",
      pointerEvents: "none",
      whiteSpace: "nowrap",
      display: "flex",
      alignItems: "center",
      gap: 6,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
      <span style={{ fontSize: 12, fontWeight: 600, color: s.color }}>{s.label}</span>
    </div>
  );
}


// ─── pace banner ─────────────────────────────────────────────────────────────

function PaceBanner({ totals, weeksRemaining, reduced, isMobile }) {
  if (!totals?.plan_hours) return null;
  const pct      = totals.progress_pct ?? 0;
  const lag      = totals.lag_hours ?? 0;
  const curPw    = totals.current_pace_pairs_pw ?? 0;
  const reqPw    = totals.required_pace_pairs_pw ?? 0;
  const isBehind = lag < -4;
  const isAhead  = lag > 2;
  const barColor = isBehind ? "#f59e0b" : isAhead ? "#22c55e" : "#3b82f6";
  const lagColor = isBehind ? "#d97706" : isAhead ? "#16a34a" : "#64748b";

  return (
    <div style={{
      margin: isMobile ? "0 10px 8px" : "0 16px 10px",
      padding: "10px 12px",
      background: "#f8fafc",
      borderRadius: 12,
      border: "1px solid #e2e8f0",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
        <div style={{ flex: 1, height: 5, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: `${Math.min(pct, 100)}%`,
            background: barColor,
            borderRadius: 999,
            transition: reduced ? "none" : "width 600ms ease-out",
          }} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", minWidth: 36, textAlign: "right" }}>
          {pct}%
        </span>
        {Math.abs(lag) >= 1 && (
          <span style={{ fontSize: 11, fontWeight: 600, color: lagColor, minWidth: 40 }}>
            {lag > 0 ? "+" : ""}{lag} ч
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: isMobile ? 12 : 20, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, color: "#64748b" }}>
          Темп <span style={{ fontWeight: 700, color: "#0f172a" }}>{curPw}</span> п/нед
        </div>
        <div style={{ fontSize: 11, color: "#64748b" }}>
          Нужно <span style={{ fontWeight: 700, color: isBehind ? "#d97706" : "#0f172a" }}>{reqPw}</span> п/нед
        </div>
        {weeksRemaining !== undefined && weeksRemaining !== null && (
          <div style={{ fontSize: 11, color: "#94a3b8" }}>
            {weeksRemaining > 0 ? `ещё ${weeksRemaining} нед` : "семестр завершён"}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── group calendar grid ──────────────────────────────────────────────────────


const DAY_NAMES = ["Пн","Вт","Ср","Чт","Пт","Сб","Вс"];

function SkeletonCalendar({ isMobile }) {
  return (
    <div style={{ padding: isMobile ? "8px 10px 14px" : "8px 14px 16px" }}>
      <div style={{ display: "flex", gap: 4, padding: "0 0 8px", overflowX: "auto" }}>
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="animate-pulse" style={{ flexShrink: 0, borderRadius: 999, height: 24, width: 36, background: "#e2e8f0" }} />
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {Array.from({ length: 35 }, (_, i) => (
          <div key={i} className="animate-pulse" style={{ borderRadius: 6, height: isMobile ? 28 : 34, background: i % 3 === 0 ? "#e2e8f0" : "#f1f5f9" }} />
        ))}
      </div>
    </div>
  );
}

function GroupCalendarGrid({ data, loading, reduced, isMobile, selTypes }) {
  const [selMonth, setSelMonth] = useState(null);
  const [hovDay, setHovDay] = useState(null);
  const [tipPos, setTipPos] = useState(null);
  const [localFilter, setLocalFilter] = useState(new Set());
  const [hovType, setHovType] = useState(null);

  const toggleLocalType = (t) => {
    if (isMobile && navigator.vibrate) navigator.vibrate(10);
    setLocalFilter(prev => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  };

  useEffect(() => { setLocalFilter(new Set()); setHovType(null); }, [selMonth]);

  useEffect(() => {
    if (!hovDay) return;
    const dismiss = () => { setHovDay(null); setTipPos(null); };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [hovDay]);

  const months = data?.months || [];

  const acadSort = (a, b) => {
    const moA = a.slice(5, 7), moB = b.slice(5, 7);
    const ayA = parseInt(moA) >= 9 ? parseInt(a.slice(0, 4)) : parseInt(a.slice(0, 4)) - 1;
    const ayB = parseInt(moB) >= 9 ? parseInt(b.slice(0, 4)) : parseInt(b.slice(0, 4)) - 1;
    if (ayA !== ayB) return ayA - ayB;
    return ACAD_MONTH_ORDER.indexOf(moA) - ACAD_MONTH_ORDER.indexOf(moB);
  };

  const sortedMonths = [...months].sort((a, b) => acadSort(a.month, b.month));

  useEffect(() => {
    if (!sortedMonths.length) return;
    const withData = sortedMonths.filter(m => Object.keys(m.days || {}).length > 0);
    const target = withData.length
      ? withData[withData.length - 1].month
      : sortedMonths[sortedMonths.length - 1].month;
    setSelMonth(prev => {
      if (prev && sortedMonths.find(m => m.month === prev)) return prev;
      return target;
    });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentMonthData = months.find(m => m.month === selMonth);
  const dayLookup = currentMonthData?.days || {};

  const effectiveFilter = hovType
    ? new Set([hovType])
    : localFilter.size > 0 ? localFilter : selTypes;

  const renderBigCalendar = () => {
    if (!selMonth) return null;
    const [y, mo] = selMonth.split("-").map(Number);
    const daysInMonth = new Date(y, mo, 0).getDate();
    const firstDayMon = (new Date(y, mo - 1, 1).getDay() + 6) % 7;

    const weeks = [];
    let week = Array(firstDayMon).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
      week.push(d);
      if (week.length === 7) { weeks.push(week); week = []; }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      weeks.push(week);
    }

    return (
      <div style={{ padding: isMobile ? "0 10px 10px" : "0 12px 8px" }}>
        {/* Week header */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 4 }}>
          {DAY_NAMES.map((d, i) => (
            <div key={d} style={{
              fontSize: isMobile ? 10 : 10, fontWeight: 600,
              color: i >= 5 ? "#d1d5db" : "#94a3b8",
              textAlign: "center", padding: "4px 0",
            }}>{d}</div>
          ))}
        </div>

        {/* Days */}
        {weeks.map((wk, wi) => (
          <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: isMobile ? 2 : 2, marginBottom: isMobile ? 2 : 2 }}>
            {wk.map((d, di) => {
              if (!d) return <div key={`e-${wi}-${di}`} />;
              const iso = `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
              const dayData = dayLookup[iso];
              const isWeekend = di >= 5;
              const isHov = hovDay === iso;

              // dominant type for coloring (overall)
              let domType = null;
              if (dayData?.by_type) {
                let maxCnt = 0;
                for (const [t, cnt] of Object.entries(dayData.by_type)) {
                  if (cnt > maxCnt) { maxCnt = cnt; domType = t; }
                }
              }

              // when filter active: color by dominant among selected types
              let colorType = domType;
              if (effectiveFilter?.size > 0 && dayData?.by_type) {
                let maxSel = 0;
                for (const t of effectiveFilter) {
                  const cnt = dayData.by_type[t] || 0;
                  if (cnt > maxSel) { maxSel = cnt; colorType = t; }
                }
              }
              const meta = colorType ? TYPE_META[colorType] : null;

              // dim days with 0 pairs of selected types
              const hasSelType = !effectiveFilter?.size || !dayData
                ? false
                : [...effectiveFilter].some(t => (dayData.by_type?.[t] || 0) > 0);
              const isDimmed = effectiveFilter?.size > 0 && dayData && !hasSelType;

              // count to show: filtered total when filter active
              const displayTotal = effectiveFilter?.size > 0 && dayData
                ? [...effectiveFilter].reduce((s, t) => s + (dayData.by_type?.[t] || 0), 0)
                : dayData?.total;

              return (
                <div
                  key={iso}
                  onPointerEnter={e => {
                    if (e.pointerType === "touch" || !dayData) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTipPos({ x: rect.left + rect.width / 2, y: rect.top });
                    setHovDay(iso);
                  }}
                  onPointerLeave={e => { if (e.pointerType === "touch") return; setHovDay(null); setTipPos(null); }}
                  onPointerDown={e => {
                    if (e.pointerType !== "touch" || !dayData) return;
                    e.stopPropagation();
                    if (hovDay === iso) { setHovDay(null); setTipPos(null); return; }
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTipPos({ x: rect.left + rect.width / 2, y: rect.top });
                    setHovDay(iso);
                  }}
                  style={{
                    borderRadius: isMobile ? 7 : 10,
                    background: isHov ? (meta?.bg || "#f0f9ff") : isDimmed ? "transparent" : meta ? meta.bg : "transparent",
                    border: `1.5px solid ${isHov ? (meta?.border || "#bae6fd") : (!isDimmed && meta) ? meta.border : "transparent"}`,
                    padding: isMobile ? "9px 2px" : "5px 2px",
                    textAlign: "center",
                    cursor: dayData ? "pointer" : "default",
                    opacity: isDimmed ? 0.3 : 1,
                    transition: reduced ? "none" : "background 80ms, border-color 80ms, opacity 150ms",
                    userSelect: "none",
                  }}
                >
                  <div style={{
                    fontSize: isMobile ? 13 : 13,
                    fontWeight: dayData ? 700 : 400,
                    lineHeight: 1.1,
                    color: isHov ? (meta?.text || "#0369a1")
                      : meta && !isDimmed ? meta.text
                      : isWeekend ? "#d1d5db"
                      : "#94a3b8",
                  }}>{d}</div>
                  {dayData && displayTotal > 0 && (
                    <div style={{
                      fontSize: isMobile ? 11 : 12, fontWeight: 700, lineHeight: 1, marginTop: 3,
                      color: meta?.text || "#64748b",
                    }}>{displayTotal}п</div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {/* Monthly type totals */}
        {(() => {
          const totals = {};
          for (const dd of Object.values(dayLookup)) {
            if (!dd?.by_type) continue;
            for (const [t, cnt] of Object.entries(dd.by_type)) {
              totals[t] = (totals[t] || 0) + cnt;
            }
          }
          const shownTypes = [
            ...TYPE_ORDER.filter(t => totals[t] > 0),
            ...Object.keys(totals).filter(t => !TYPE_ORDER.includes(t) && totals[t] > 0),
          ];
          if (!shownTypes.length) return null;
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "14px 0 6px" }}>
              {shownTypes.map(t => {
                const meta = TYPE_META[t];
                const isActive = localFilter.has(t);
                const isHovered = hovType === t;
                return (
                  <div
                    key={t}
                    onClick={() => toggleLocalType(t)}
                    onMouseEnter={() => !isMobile && setHovType(t)}
                    onMouseLeave={() => !isMobile && setHovType(null)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      background: (isActive || isHovered) ? (meta?.bg || "#f0f9ff") : "#f8fafc",
                      border: `1.5px solid ${(isActive || isHovered) ? (meta?.border || "#bae6fd") : "#e2e8f0"}`,
                      borderRadius: 10, padding: "5px 11px",
                      cursor: "pointer",
                      userSelect: "none", WebkitUserSelect: "none",
                      transform: isActive ? "scale(1.04)" : "scale(1)",
                      transition: reduced ? "none" : "all 140ms ease",
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: isActive ? 700 : 600, color: meta?.text || "#64748b" }}>{meta?.label || t}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: meta?.text || "#475569" }}>{totals[t]}&thinsp;п.</span>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    );
  };

  if (loading) return <SkeletonCalendar isMobile={isMobile} />;

  if (!sortedMonths.length) return (
    <div style={{ padding: "32px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
      Нет данных
    </div>
  );

  const hovDayData = hovDay ? (() => {
    const mk = hovDay.slice(0, 7);
    const m = months.find(x => x.month === mk);
    return m?.days?.[hovDay] || null;
  })() : null;

  return (
    <div style={{ borderBottom: isMobile ? "1px solid #f1f5f9" : "none" }}>
      {/* Month strip */}
      <div style={{ position: "relative" }}>
        <div style={{
          display: "flex", gap: isMobile ? 2 : 3, overflowX: "auto",
          padding: isMobile ? "10px 10px 6px" : "10px 12px 4px",
          scrollbarWidth: "none", msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}>
          {sortedMonths.map(m => {
            const hasData = Object.keys(m.days || {}).length > 0;
            const isSel = m.month === selMonth;
            const mo = parseInt(m.month.slice(5));
            const y = parseInt(m.month.slice(0, 4));
            const showYear = mo === 9 || mo === 1;
            return (
              <button
                key={m.month}
                onClick={() => hasData && setSelMonth(m.month)}
                style={{
                  flexShrink: 0,
                  padding: isMobile ? "6px 13px" : "4px 10px",
                  borderRadius: 999,
                  fontSize: isMobile ? 12 : 11,
                  fontWeight: isSel ? 700 : hasData ? 500 : 400,
                  color: isSel ? "#1d4ed8" : hasData ? "#64748b" : "#cbd5e1",
                  background: isSel ? "#eff6ff" : "transparent",
                  border: `1.5px solid ${isSel ? "#bfdbfe" : "transparent"}`,
                  cursor: hasData ? "pointer" : "default",
                  opacity: hasData ? 1 : 0.35,
                  outline: "none",
                  transition: reduced ? "none" : "all 120ms ease",
                  whiteSpace: "nowrap",
                }}
              >
                {MONTH_SHORT[m.month.slice(5)]}{showYear ? ` '${String(y).slice(2)}` : ""}
              </button>
            );
          })}
        </div>
        {isMobile && (
          <div style={{
            position: "absolute", right: 0, top: 0, bottom: 0, width: 36,
            background: "linear-gradient(to right, transparent, white)",
            pointerEvents: "none",
          }} />
        )}
      </div>

      {/* Big calendar */}
      {renderBigCalendar()}

      {/* Tooltip — bottom sheet on mobile, floating on desktop */}
      {hovDayData && createPortal(
        isMobile ? (
          <div
            onPointerDown={e => e.stopPropagation()}
            style={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 9000,
              background: "#fff", borderRadius: "16px 16px 0 0", padding: "14px 18px 28px",
              boxShadow: "0 -4px 24px rgba(15,23,42,0.12), 0 0 0 1px #e2e8f0",
            }}
          >
            <div style={{ width: 36, height: 4, borderRadius: 2, background: "#e2e8f0", margin: "0 auto 12px" }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>
              {new Date(hovDay + "T00:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}
            </div>
            {TYPE_ORDER.filter(t => (hovDayData.by_type[t] || 0) > 0).map(t => {
              const meta = TYPE_META[t];
              return (
                <div key={t} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: meta?.text || "#94a3b8", flexShrink: 0 }} />
                  <span style={{ fontSize: 14, color: "#475569", flex: 1 }}>{meta?.label || t}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{hovDayData.by_type[t]}</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>п.</span>
                </div>
              );
            })}
            {Object.entries(hovDayData.by_type).filter(([t, cnt]) => !TYPE_ORDER.includes(t) && cnt > 0).map(([t, cnt]) => (
              <div key={t} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: "#94a3b8", flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: "#475569", flex: 1 }}>{t}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{cnt}</span>
                <span style={{ fontSize: 12, color: "#94a3b8" }}>п.</span>
              </div>
            ))}
            <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 6, paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "#94a3b8" }}>Итого</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.3px" }}>{hovDayData.total} п.</span>
            </div>
          </div>
        ) : tipPos ? (
          <div style={{
            position: "fixed",
            left: Math.min(Math.max(80, tipPos.x), window.innerWidth - 80),
            top: tipPos.y - 8,
            transform: "translate(-50%, -100%)",
            zIndex: 9000,
            background: "#fff", borderRadius: 12, padding: "10px 14px",
            boxShadow: "0 8px 28px -6px rgba(15,23,42,0.2), 0 0 0 1px #e2e8f0",
            pointerEvents: "none", minWidth: 148,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
              {new Date(hovDay + "T00:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}
            </div>
            {TYPE_ORDER.filter(t => (hovDayData.by_type[t] || 0) > 0).map(t => {
              const meta = TYPE_META[t];
              return (
                <div key={t} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 2, background: meta?.text || "#94a3b8", flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: "#475569", flex: 1 }}>{meta?.label || t}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{hovDayData.by_type[t]}</span>
                  <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: 1 }}>п.</span>
                </div>
              );
            })}
            {Object.entries(hovDayData.by_type).filter(([t, cnt]) => !TYPE_ORDER.includes(t) && cnt > 0).map(([t, cnt]) => (
              <div key={t} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: "#94a3b8", flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "#475569", flex: 1 }}>{t}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{cnt}</span>
                <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: 1 }}>п.</span>
              </div>
            ))}
            <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 5, paddingTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>Итого</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.3px" }}>{hovDayData.total} п.</span>
            </div>
          </div>
        ) : null,
        document.body
      )}
    </div>
  );
}

// ─── unified discipline list ──────────────────────────────────────────────────

function SkeletonDonut({ ds = 96 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "4px 2px" }}>
      <div className="animate-pulse" style={{
        width: ds, height: ds, borderRadius: "50%",
        background: "conic-gradient(#e2e8f0 0%, #e2e8f0 100%)",
        mask: `radial-gradient(circle, transparent ${ds * 0.28}px, black ${ds * 0.29}px)`,
        WebkitMask: `radial-gradient(circle, transparent ${ds * 0.28}px, black ${ds * 0.29}px)`,
      }} />
      <div className="animate-pulse" style={{ marginTop: 10, height: 11, width: 88, borderRadius: 6, background: "#e2e8f0" }} />
    </div>
  );
}

function UnifiedDisciplineList({ stats, planData, loading, planLoading, fetching, morphOut, reduced, mounted, selTypes, isMobile }) {
  const { isDark } = useTheme();
  const [selDisc, setSelDisc] = useState(null);
  const [expandedDisc, setExpandedDisc] = useState(null); // delayed — fires after others fade out
  const [hovDisc, setHovDisc] = useState(null);
  const [ctrlTip, setCtrlTip] = useState(null);
  const gridRef = useRef(null);
  const expandTimer = useRef(null);
  const prevVisibleCountRef = useRef(2);
  const statsList = stats?.stats || [];

  const selectDisc = (disc) => {
    if (expandTimer.current) clearTimeout(expandTimer.current);
    if (!disc) {
      setExpandedDisc(null);
      setSelDisc(null);
      return;
    }
    setSelDisc(disc);
    // Wait for others to fade (160ms), then reflow grid
    expandTimer.current = setTimeout(() => setExpandedDisc(disc), 160);
  };

  const deselect = () => {
    if (expandTimer.current) clearTimeout(expandTimer.current);
    setExpandedDisc(null);
    setSelDisc(null);
    setTimeout(() => {
      gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  };

  const planByDisc = new Map();
  for (const item of planData?.disciplines || []) {
    if (!planByDisc.has(item.discipline)) planByDisc.set(item.discipline, []);
    planByDisc.get(item.discipline).push(item);
  }

  const planRows = [...planByDisc.entries()].map(([disc, items]) => {
    // Fall back to conducted_hours_excel (plan−remaining from vychitka) when archive hasn't matched
    const effectiveItems = items.map(x => {
      const conducted = x.conducted_hours_excel ?? 0;
      return {
        ...x,
        archive_hours: x.archive_hours > 0 ? x.archive_hours : conducted,
        archive_pairs: x.archive_pairs > 0 ? x.archive_pairs : Math.round(conducted / 2),
      };
    });
    const pairCount = effectiveItems.reduce((s, x) => s + (x.archive_pairs ?? Math.round(x.archive_hours / 2)), 0);
    const totalPlan = effectiveItems.reduce((s, x) => s + x.plan_hours, 0);
    const totalArchive = effectiveItems.reduce((s, x) => s + x.archive_hours, 0);
    const pct = totalPlan > 0 ? Math.round(totalArchive / totalPlan * 100) : null;
    const controlForm = items[0]?.control_form ?? null;
    return { disc, items: effectiveItems, pairCount, pct, hasPlan: true, totalArchive, totalPlan, controlForm };
  }).filter(row => row.totalPlan > 0);

  const planDiscKeys = new Set([...planByDisc.keys()].map(k => k.toLowerCase().trim()));
  const orphanRows = statsList
    .filter(s => !planDiscKeys.has(s.discipline.toLowerCase().trim()) && s.count > 0)
    .map(s => ({
      disc: s.discipline,
      items: [],
      archiveItems: (s.by_type || []).map(bt => ({ lesson_type: bt.lesson_type, count: bt.count })),
      pairCount: s.count,
      pct: null,
      hasPlan: false,
      totalArchive: s.count,
    }));

  const allRows = [...planRows, ...orphanRows];
  allRows.sort((a, b) => {
    if (a.hasPlan && b.hasPlan) return (a.pct ?? 0) - (b.pct ?? 0);
    if (a.hasPlan) return -1;
    if (b.hasPlan) return 1;
    return b.pairCount - a.pairCount;
  });

  if (!morphOut && allRows.length > 0) prevVisibleCountRef.current = allRows.length;

  const anim = reduced ? {} : {
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(10px)",
    transition: `opacity 200ms ${SP}, transform 200ms ${SP}`,
  };

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", padding: "28px 0" }}>
      <div className="w-5 h-5 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
    </div>
  );

  if (!statsList.length) return (
    <p className="text-center text-[13px] text-slate-400 py-6 px-4">Проведённые занятия не найдены</p>
  );


  return (
    <div style={{ paddingBottom: 20, position: "relative", ...anim }}>
      {!morphOut && fetching && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 2,
          background: "rgba(255,255,255,0.65)", backdropFilter: "blur(2px)",
          display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8,
        }}>
          <div className="w-4 h-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
        </div>
      )}

      {/* skeleton grid — shown in-flow while morphOut so container keeps height */}
      {morphOut && (
        <div style={{
          display: "grid",
          gridTemplateColumns: prevVisibleCountRef.current === 1 ? "1fr" : prevVisibleCountRef.current === 3 ? "1fr 1fr 1fr" : "1fr 1fr",
          gap: 10,
          padding: "0 12px 8px",
        }}>
          {Array.from({ length: prevVisibleCountRef.current }, (_, i) => (
            <SkeletonDonut key={i} ds={96} />
          ))}
        </div>
      )}

      {/* ── discipline rows (morphism grid) ── */}
      <div ref={gridRef} style={{
        display: morphOut ? "none" : "grid",
        gridTemplateColumns: allRows.length === 1 ? "1fr" : (isMobile || allRows.length === 2) ? "1fr 1fr" : allRows.length === 3 ? "1fr 1fr 1fr" : "1fr 1fr",
        gap: selDisc ? 0 : (isMobile ? 6 : 10),
        padding: isMobile ? "0 8px 8px" : "0 12px 8px",
        transition: "gap 300ms ease-out",
      }}>
        {allRows.map((row, i) => {
          const isSelected = selDisc === row.disc;
          const isExpanded = expandedDisc === row.disc;
          const isDimmedByDisc = !!selDisc && !isSelected;

          // type filter: dim in-place
          const isFiltered = selTypes.size > 0 && !selDisc && (
            row.hasPlan
              ? !row.items.some(x => selTypes.has(x.lesson_type) && x.archive_hours > 0)
              : !(row.archiveItems || []).some(x => selTypes.has(x.lesson_type))
          );

          // collapse happens immediately on selDisc (others fade), expand after delay
          const isCollapsed = isDimmedByDisc || morphOut;
          const itemOpacity = isCollapsed ? 0 : isFiltered ? 0.2 : (mounted || reduced ? 1 : 0);
          const itemMaxH = isCollapsed ? 0 : isExpanded ? 600 : 420;
          const itemPad = isCollapsed ? "0 2px" : isExpanded ? "4px 2px 12px" : "4px 2px";

          const outerTransition = (isDimmedByDisc || morphOut)
            ? "opacity 160ms ease-out, max-height 360ms ease-out 140ms, padding 360ms ease-out 140ms"
            : isFiltered
              ? "opacity 200ms ease-out"
              : reduced
                ? "none"
                : `opacity 260ms ${SP} ${i * 30}ms`;

          // gridColumn and scale use expandedDisc (delayed) so reflow is invisible
          const donutScale = isExpanded ? "scale(1)" : isCollapsed ? "scale(0.58)" : (mounted || reduced ? "scale(1)" : "scale(0.85)");
          const donutTranslate = (!selDisc && !mounted && !reduced) ? " translateY(14px)" : "";
          const donutTransform = donutScale + donutTranslate;
          const donutTransition = expandedDisc
            ? isExpanded
              ? "transform 420ms cubic-bezier(0.34,1.4,0.64,1)"
              : "transform 280ms ease-out"
            : reduced
              ? "none"
              : `transform 260ms ${SP} ${i * 30}ms`;

          return (
            <div key={row.disc} style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              padding: itemPad,
              opacity: itemOpacity,
              maxHeight: itemMaxH,
              overflow: isCollapsed ? "hidden" : "visible",
              pointerEvents: (isCollapsed || isFiltered) ? "none" : "auto",
              gridColumn: isExpanded ? "1 / -1" : undefined,
              transition: outerTransition,
              position: "relative",
              zIndex: hovDisc === row.disc ? 100 : "auto",
            }}>
              <div style={{
                transform: donutTransform,
                transition: donutTransition,
                transformOrigin: "center center",
                display: "flex", flexDirection: "column", alignItems: "center",
              }}>
                {row.hasPlan ? (
                  <DisciplineDonut
                    items={row.items}
                    selTypes={selTypes}
                    reduced={reduced}
                    displaySize={isMobile ? 115 : 150}
                    isMobile={isMobile}
                    interactive={isSelected}
                    onHovChange={(a) => setHovDisc(a ? row.disc : null)}
                    onSelect={() => isSelected ? deselect() : selectDisc(row.disc)}
                  />
                ) : (
                  <ArchiveDonut
                    items={row.archiveItems}
                    pairCount={row.pairCount}
                    reduced={reduced}
                    displaySize={isMobile ? 115 : 150}
                    isMobile={isMobile}
                    selTypes={selTypes}
                    interactive={isSelected}
                    onHovChange={(a) => setHovDisc(a ? row.disc : null)}
                    onSelect={() => isSelected ? deselect() : selectDisc(row.disc)}
                  />
                )}
                <div style={{ marginTop: 7, textAlign: "center", width: "100%", padding: "0 2px", position: "relative" }}>
                  <div
                    style={{
                      fontSize: isMobile ? 11 : 15, fontWeight: 600, color: isDark ? "#f1f5f9" : "#0f172a", lineHeight: 1.3,
                      display: "-webkit-box", WebkitLineClamp: isMobile ? 3 : 2, WebkitBoxOrient: "vertical",
                      overflow: "hidden", wordBreak: "break-word",
                      cursor: row.controlForm ? "default" : undefined,
                    }}
                    title={row.disc}
                    onPointerEnter={e => { if (row.controlForm && e.pointerType !== "touch") setCtrlTip(row.disc); }}
                    onPointerLeave={() => setCtrlTip(null)}
                    onPointerDown={e => { if (row.controlForm && e.pointerType === "touch") { e.stopPropagation(); setCtrlTip(p => p === row.disc ? null : row.disc); } }}
                  >{row.disc}</div>
                  {row.controlForm && ctrlTip === row.disc && <ControlFormTooltip form={row.controlForm} />}
                </div>
                {isExpanded && row.hasPlan && row.items.some(x => x.plan_hours > 0) && (
                  <div style={{ width: "100%", maxWidth: 340, marginTop: 16, padding: "0 6px" }}>
                    {row.items.filter(x => x.plan_hours > 0).map(item => {
                      const meta = TYPE_META[item.lesson_type];
                      const pct = Math.round(item.archive_hours / item.plan_hours * 100);
                      const isBehindItem = (item.lag_hours ?? 0) < -2;
                      const reqPw = item.required_pace_pairs_pw ?? 0;
                      const curPw = item.current_pace_pairs_pw ?? 0;
                      return (
                        <div key={item.lesson_type} style={{ marginBottom: 10 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: meta?.text || "#64748b" }}>
                              {meta?.label || item.lesson_type}
                            </span>
                            <span style={{ fontSize: 11, color: "#94a3b8" }}>
                              {item.archive_hours}/{item.plan_hours} ч · {pct}%
                            </span>
                          </div>
                          <div style={{ height: 4, background: "#e8ecf2", borderRadius: 999, overflow: "hidden" }}>
                            <div style={{
                              height: "100%",
                              width: `${Math.min(pct, 100)}%`,
                              background: PLAN_TYPE_COLOR[item.lesson_type] || "#94a3b8",
                              borderRadius: 999,
                              transition: reduced ? "none" : "width 500ms ease-out 200ms",
                            }} />
                          </div>
                          {(reqPw > 0 || curPw > 0) && (
                            <div style={{ display: "flex", gap: 12, marginTop: 3, fontSize: 10, color: "#94a3b8" }}>
                              <span>Темп <strong style={{ color: "#475569" }}>{curPw}</strong></span>
                              <span>Нужно <strong style={{ color: isBehindItem ? "#d97706" : "#475569" }}>{reqPw}</strong> п/нед</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {isExpanded && (
                  <button
                    onClick={(e) => { e.stopPropagation(); deselect(); }}
                    style={{ fontSize: 11, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", padding: "6px 8px 2px", marginTop: 8 }}
                  >← Назад</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {planLoading && planRows.length === 0 && (
        <div style={{ display: "flex", justifyContent: "center", padding: "6px 0 2px" }}>
          <div className="w-4 h-4 rounded-full border-2 border-slate-200 border-t-transparent animate-spin" />
        </div>
      )}
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function TeacherDisciplineChart({ fio, viewerGroup }) {
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [fetching, setFetching]     = useState(false);
  const [morphOut, setMorphOut]     = useState(false);
  const [groups, setGroups]         = useState([]);
  const [types, setTypes]           = useState([]);
  const [mounted, setMounted]       = useState(false);
  const source = "all";
  const [selGroup, setSelGroup]         = useState(new Set());
  const [selTypes, setSelTypes]         = useState(new Set());
  const periodRef = useRef(null);
  const [groupCalData, setGroupCalData] = useState(null);
  const [groupCalLoading, setGroupCalLoading] = useState(true);
  const [periodSheetOpen, setPeriodSheetOpen] = useState(false);

  useEffect(() => {
    if (!periodSheetOpen) return;
    const handle = (e) => { if (!periodRef.current?.contains(e.target)) setPeriodSheetOpen(false); };
    document.addEventListener("pointerdown", handle);
    return () => document.removeEventListener("pointerdown", handle);
  }, [periodSheetOpen]);
  const [vychitkaFiles, setVychitkaFiles] = useState([]);
  const [selVychitkaFile, setSelVychitkaFile] = useState(null); // null = latest
  const [planData, setPlanData] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  useEffect(() => {
    const handler = () => {
      invalidateCachedJson("plan:");
      invalidateCachedJson("vychitka:files");
      setMorphOut(true);
      setTimeout(() => {
        setMounted(false);
        setRefetchKey(k => k + 1);
      }, 320);
    };
    const storageHandler = (e) => { if (e.key === "audassist.vychitka.v") handler(); };
    window.addEventListener("vychitka:uploaded", handler);
    window.addEventListener("storage", storageHandler);
    return () => {
      window.removeEventListener("vychitka:uploaded", handler);
      window.removeEventListener("storage", storageHandler);
    };
  }, []);
  const reduced = useReducedMotion();
  const isMobile = useIsMobile();

  const surname = (() => {
    if (!fio) return "";
    const parts = fio.trim().split(/\s+/);
    let q = parts[0];
    if (parts.length > 1) q += " " + parts[1][0].toUpperCase() + ".";
    if (parts.length > 2) q += parts[2][0].toUpperCase() + ".";
    return q;
  })();

  const parseDateStr = (s) => { if (!s) return null; const [d, m, y] = s.split("."); return `${y}-${m}-${d}`; };

  // groups: cached 5 min — adapts to type filter; keeps previous list if result is empty
  useEffect(() => {
    if (!surname) return;
    const p = new URLSearchParams({ teacher: surname });
    if (source !== "all") p.set("source", source);
    for (const t of selTypes) p.append("lesson_type", t);
    const cacheKey = `groups:${surname}:${source}:${[...selTypes].sort().join(",")}`;
    fetchJsonCached(`/export/groups?${p}`, { cacheKey, ttlMs: 5 * 60_000 })
      .then(g => {
        const next = Array.isArray(g) ? g : [];
        setGroups(prev => next.length > 0 ? next : prev);
      })
      .catch(() => {});
  }, [surname, source, selTypes]);

  // vychitka files: cached 2 min, refetch on vychitka change
  useEffect(() => {
    fetchJsonCached("/export/vychitka/files", { cacheKey: "vychitka:files", ttlMs: 2 * 60_000, force: refetchKey > 0 })
      .then(f => setVychitkaFiles(Array.isArray(f) ? f : []))
      .catch(() => setVychitkaFiles([]));
  }, [refetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // types: cached 5 min
  useEffect(() => {
    if (!surname) return;
    const p = new URLSearchParams({ teacher: surname });
    if (source !== "all") p.set("source", source);
    for (const g of selGroup) p.append("group", g);
    fetchJsonCached(`/export/lesson-types?${p}`, { cacheKey: `types:${surname}:${source}:${[...selGroup].join(",")}`, ttlMs: 5 * 60_000 })
      .then(t => {
        const newTypes = (Array.isArray(t) ? t : []).filter(x => TYPE_META[x]);
        setTypes(newTypes);
        setSelTypes(prev => new Set([...prev].filter(x => newTypes.includes(x))));
      })
      .catch(() => {});
  }, [surname, source, selGroup]);

  // plan + stats: parallel when plan cached, sequential otherwise; SWR, 5 min TTL
  useEffect(() => {
    if (!surname) return;
    let cancelled = false;
    setLoading(true);
    setFetching(true);
    setPlanLoading(true);

    async function load() {
      try {
        const planP = new URLSearchParams({ teacher: surname });
        const groupParam = viewerGroup || (selGroup.size === 1 ? [...selGroup][0] : "");
        if (groupParam) planP.set("group", groupParam);
        else for (const g of selGroup) planP.append("group", g);
        if (selVychitkaFile) planP.set("vychitka_file", selVychitkaFile.filename);
        const planCacheKey = `plan:${surname}:${planP}`;

        // если plan уже в кэше — вычисляем statsP сразу и запускаем параллельно
        const cachedPlan = peekCachedJson(planCacheKey, 5 * 60_000);

        const buildStatsP = (plan) => {
          const p = new URLSearchParams({ teacher: surname, source });
          for (const g of selGroup) p.append("group", g);
          const dateFrom = plan?.semester_start ?? null;
          const dateTo = selVychitkaFile
            ? parseDateStr(selVychitkaFile.date_str)
            : (plan?.semester_end ?? null);
          if (dateFrom) p.append("date_from", dateFrom);
          if (dateTo) p.append("date_to", dateTo);
          return p;
        };

        let plan = null;
        let data = null;

        if (cachedPlan !== null) {
          const statsP = buildStatsP(cachedPlan);
          [plan, data] = await Promise.all([
            fetchJsonCached(`/export/teacher-plan-progress?${planP}`, { cacheKey: planCacheKey, ttlMs: 5 * 60_000, swr: true }).catch(() => null),
            fetchJsonCached(`/export/teacher-discipline-stats?${statsP}`, { cacheKey: `stats:${surname}:${statsP}`, ttlMs: 5 * 60_000, swr: true }).catch(() => null),
          ]);
        } else {
          try { plan = await fetchJsonCached(`/export/teacher-plan-progress?${planP}`, { cacheKey: planCacheKey, ttlMs: 5 * 60_000, swr: true }); } catch {}
          if (cancelled) return;
          const statsP = buildStatsP(plan);
          data = await fetchJsonCached(`/export/teacher-discipline-stats?${statsP}`, { cacheKey: `stats:${surname}:${statsP}`, ttlMs: 5 * 60_000, swr: true }).catch(() => null);
        }

        if (cancelled) return;
        setPlanData(plan || null);
        setPlanLoading(false);
        setStats(data);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFetching(false);
          setMounted(true);
          setMorphOut(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [surname, source, viewerGroup, selGroup, selVychitkaFile, refetchKey]);

  const resetAnim = () => setMounted(false);

  useEffect(() => {
    if (!surname) return;
    let cancelled = false;
    setGroupCalLoading(true);
    const p = new URLSearchParams({ teacher: surname });
    for (const g of selGroup) p.append("group", g);
    const cacheKey = `tcal:${surname}:${[...selGroup].sort().join(",")}`;
    fetchJsonCached(`/export/teacher-monthly-stats?${p}`, { cacheKey, ttlMs: 5 * 60_000 })
      .then(d => { if (!cancelled) { setGroupCalData(d || null); setGroupCalLoading(false); } })
      .catch(() => { if (!cancelled) { setGroupCalData(null); setGroupCalLoading(false); } });
    return () => { cancelled = true; };
  }, [surname, selGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ transition: reduced ? "none" : "opacity 280ms ease", opacity: morphOut ? 0.35 : 1, position: "relative" }}>
      {/* ── filters ── */}
      <div style={{ padding: isMobile ? "8px 10px 10px" : "10px 16px 12px", display: "flex", gap: isMobile ? 4 : 6, alignItems: "center", flexWrap: "wrap", position: "relative", zIndex: 200 }}>
        {types.length > 0 && (
          <div style={{ flexShrink: 0 }}>
            <TypeChips
              types={types} selected={selTypes} reduced={reduced} isMobile={isMobile}
              onToggle={t => {
                setSelTypes(prev => {
                  const next = new Set(prev);
                  next.has(t) ? next.delete(t) : next.add(t);
                  return next;
                });
              }}
              onClear={() => { setSelTypes(new Set()); }}
            />
          </div>
        )}
        {!viewerGroup && (
          <GroupPicker groups={groups} selected={selGroup} reduced={reduced} isMobile={isMobile}
            onToggle={g => {
              setMorphOut(true);
              setTimeout(() => {
                setSelGroup(prev => {
                  const next = new Set(prev);
                  next.has(g) ? next.delete(g) : next.add(g);
                  return next;
                });
                resetAnim();
              }, 320);
            }}
            onClear={() => {
              setMorphOut(true);
              setTimeout(() => { setSelGroup(new Set()); resetAnim(); }, 320);
            }}
          />
        )}
        {vychitkaFiles.length > 0 && (
          <div style={{ flexShrink: 0 }}>
          <div ref={periodRef} style={{ position: "relative", flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setPeriodSheetOpen(o => !o)}
              style={{
                height: isMobile ? 24 : 28, display: "flex", alignItems: "center", gap: isMobile ? 4 : 5,
                padding: isMobile ? "0 8px" : "0 10px", borderRadius: 999, fontSize: isMobile ? 10 : 11,
                fontWeight: 600, letterSpacing: "0.02em", whiteSpace: "nowrap",
                border: selVychitkaFile ? "1.5px solid #bfdbfe" : "1.5px solid transparent",
                background: selVychitkaFile ? "#eff6ff" : "#eef2f7",
                color: selVychitkaFile ? "#1d4ed8" : "#64748b",
                cursor: "pointer", outline: "none",
                transform: (periodSheetOpen || selVychitkaFile) ? "scale(1.03)" : "scale(1)",
                transition: reduced ? "none" : `all 150ms ${SP}`,
              }}
            >
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                <rect x="1" y="2" width="10" height="9" rx="2" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M1 5h10M4 1v2M8 1v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
              {selVychitkaFile ? (selVychitkaFile.date_str ? `На ${selVychitkaFile.date_str}` : selVychitkaFile.filename.replace(/\.xlsx$/i, "")) : "Весь период"}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{
                flexShrink: 0,
                transform: periodSheetOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: reduced ? "none" : `transform 220ms ${SP_OVER}`,
              }}>
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <div style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 30,
              minWidth: 200,
              display: "grid",
              gridTemplateRows: periodSheetOpen ? "1fr" : "0fr",
              transition: reduced ? "none" : `grid-template-rows 220ms ${SP}`,
              overflow: "hidden",
              borderRadius: 14,
            }}>
              <div style={{
                overflow: "hidden",
                background: "#fff",
                borderRadius: 14,
                boxShadow: "0 0 0 1px #e2e8f0, 0 8px 24px -6px rgba(15,23,42,0.14)",
              }}>
                <div style={{ padding: "6px 0" }}>
                  <button
                    onClick={() => { setSelVychitkaFile(null); setMounted(false); setPeriodSheetOpen(false); }}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      width: "100%", padding: "8px 14px", fontSize: 12, fontWeight: !selVychitkaFile ? 700 : 500,
                      color: !selVychitkaFile ? "#1d4ed8" : "#64748b",
                      background: "none", border: "none", borderBottom: "1px solid #f1f5f9",
                      cursor: "pointer", outline: "none", textAlign: "left",
                    }}
                  >
                    <span>Весь период</span>
                    {!selVychitkaFile && (
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
                        <path d="M2 6.5L5 9.5L11 3.5" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                  {vychitkaFiles.map(f => {
                    const on = selVychitkaFile?.filename === f.filename;
                    return (
                      <button key={f.filename} onClick={() => { setSelVychitkaFile(f); setMounted(false); setPeriodSheetOpen(false); }} style={{
                        display: "flex", alignItems: "center", gap: 8,
                        width: "100%", padding: "8px 14px", fontSize: 12,
                        fontWeight: on ? 600 : 400, color: on ? "#2563eb" : "#0f172a",
                        background: on ? "#f0f7ff" : "none",
                        border: "none", cursor: "pointer", outline: "none", textAlign: "left",
                        transition: reduced ? "none" : `background 120ms ${SP}, color 120ms ${SP}`,
                      }}>
                        <span style={{ flex: 1 }}>{f.date_str ? `На ${f.date_str}` : f.filename.replace(/\.xlsx$/i, "")}{f.is_latest ? <span style={{ marginLeft: 6, fontSize: 10, color: "#94a3b8", fontWeight: 400 }}>последняя</span> : null}</span>
                        {on && (
                          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
                            <path d="M2 6.5L5 9.5L11 3.5" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          </div>
        )}
      </div>


      {/* ── pace banner ── */}
      {planData?.totals && (
        <div style={{ position: "relative", zIndex: 2 }}>
          <PaceBanner
            totals={planData.totals}
            weeksRemaining={planData.weeks_remaining}
            reduced={reduced}
            isMobile={isMobile}
          />
        </div>
      )}

      {/* ── two-panel layout ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        alignItems: "start",
      }}>
        <div style={{ borderRight: isMobile ? "none" : "1px solid #f1f5f9", order: isMobile ? 2 : 0 }}>
          <UnifiedDisciplineList
            stats={stats}
            planData={planData}
            loading={loading}
            planLoading={planLoading}
            fetching={fetching}
            morphOut={morphOut}
            reduced={reduced}
            mounted={mounted}
            selTypes={selTypes}
            isMobile={isMobile}
          />
        </div>
        <div style={{ order: isMobile ? 1 : 0, minWidth: 0 }}>
          <GroupCalendarGrid
            data={groupCalData}
            loading={groupCalLoading}
            reduced={reduced}
            isMobile={isMobile}
            selTypes={selTypes}
          />
        </div>
      </div>
    </div>
  );
}
